// @flow

import React, { useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import * as Actions from 'main-app/store/Actions';
import { i18n, getCompanyCurrency } from 'shared/utils';
import { graphql, QueryRenderer } from 'react-relay';
import relayEnvironment from 'shared/gql/relayEnvironment';
import AddJournalEntryMutation from 'main-app/mutations/AddJournalEntry';
import Button from 'shared/components/common/Button';
import Loader from 'shared/components/common/Loader';
import Icon from 'shared/components/common/Icon';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'shared/components/modal';
import {
  Formik,
  Form,
  FieldGroup,
  FieldGroupRow,
} from 'shared/components/form';

const LinesWrapper = styled.div`
  margin-top: 16px;
`;

const LineRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const LineField = styled.div`
  flex: ${({ flex }) => flex || 1};
`;

const LineLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #666;
  margin-bottom: 4px;
`;

const LineInput = styled.input`
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  &:focus {
    outline: none;
    border-color: #2196f3;
  }
`;

const LineSelect = styled.select`
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: #fff;
  &:focus {
    outline: none;
    border-color: #2196f3;
  }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #C62828;
  padding: 4px;
  &:hover {
    opacity: 0.7;
  }
`;

const AddLineButton = styled.button`
  background: none;
  border: 1px dashed #ccc;
  border-radius: 4px;
  padding: 8px 16px;
  color: #2196f3;
  cursor: pointer;
  font-size: 13px;
  width: 100%;
  margin-top: 8px;
  &:hover {
    background: #f5f5f5;
  }
`;

const TotalsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 24px;
  margin-top: 12px;
  padding: 8px 0;
  border-top: 2px solid #eee;
  font-weight: 600;
  font-size: 14px;
`;

const TotalLabel = styled.span`
  color: #666;
`;

const TotalValue = styled.span`
  color: ${({ balanced }) => (balanced ? '#2E7D32' : '#C62828')};
  min-width: 100px;
  text-align: right;
`;

const SectionTitle = styled.h4`
  margin: 16px 0 8px;
  color: #333;
  font-size: 14px;
`;

type Props = {
  onClose: () => void,
};

const emptyLine = () => ({
  glAccountId: '',
  debit: '',
  credit: '',
  memo: '',
});

const AddJournalEntryModal = ({ onClose }: Props) => {
  const [lines, setLines] = useState([emptyLine(), emptyLine()]);

  const handleLineChange = (index: number, field: string, value: string) => {
    const updatedLines = [...lines];
    updatedLines[index] = { ...updatedLines[index], [field]: value };
    // If user types in debit, clear credit and vice versa
    if (field === 'debit' && value) {
      updatedLines[index].credit = '';
    } else if (field === 'credit' && value) {
      updatedLines[index].debit = '';
    }
    setLines(updatedLines);
  };

  const addLine = () => {
    setLines([...lines, emptyLine()]);
  };

  const removeLine = (index: number) => {
    if (lines.length <= 2) return;
    setLines(lines.filter((_, i) => i !== index));
  };

  const totalDebit = lines.reduce(
    (sum, l) => sum + (parseFloat(l.debit) || 0),
    0,
  );
  const totalCredit = lines.reduce(
    (sum, l) => sum + (parseFloat(l.credit) || 0),
    0,
  );
  const isBalanced =
    Math.round(totalDebit * 100) === Math.round(totalCredit * 100) &&
    totalDebit > 0;

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: getCompanyCurrency(),
    }).format(val);

  return (
    <QueryRenderer
      environment={relayEnvironment}
      cacheConfig={{ force: true }}
      query={graphql`
        query AddJournalEntryModalQuery {
          glAccounts(first: 200, isActive: true, sortBy: { field: ACCOUNT_NUMBER, direction: ASC }) {
            edges {
              node {
                id
                accountNumber
                name
                accountType
              }
            }
          }
        }
      `}
      variables={{}}
      render={({ props: queryProps }) => (
        <Modal isOpen onClose={onClose}>
          <Formik
            initialValues={{
              entryDate: new Date().toISOString().split('T')[0],
              memo: '',
            }}
            validationSchema={Yup.object().shape({
              entryDate: Yup.string().required(
                i18n.t('Entry date is required'),
              ),
            })}
            onSubmit={async (values, { setSubmitting }) => {
              const journalLines = lines
                .filter(l => l.glAccountId && (l.debit || l.credit))
                .map(l => ({
                  glAccountId: l.glAccountId,
                  debit: parseFloat(l.debit) || 0,
                  credit: parseFloat(l.credit) || 0,
                  memo: l.memo || null,
                }));

              if (journalLines.length < 2) {
                Actions.alertNotification(
                  i18n.t('At least 2 journal lines are required.'),
                  'error',
                );
                setSubmitting(false);
                return;
              }

              const input = {
                entryDate: values.entryDate,
                memo: values.memo.trim() || null,
                lines: journalLines,
              };

              try {
                await AddJournalEntryMutation.commit({
                  variables: { input },
                });
                Actions.alertNotification(
                  i18n.t('Journal entry created successfully.'),
                  'success',
                );
                onClose();
              } catch (e) {
                Actions.alertNotification(
                  i18n.t('Error creating journal entry.'),
                  'error',
                );
                setSubmitting(false);
              }
            }}
            render={({ errors, isValid, isSubmitting, handleSubmit }) => (
              <Form onSubmit={handleSubmit}>
                <ModalHeader header={i18n.t('New Journal Entry')} onClose={onClose} />
                <ModalBody withPadding>
                  <FieldGroupRow>
                    <FieldGroup
                      name="entryDate"
                      label={i18n.t('Entry Date')}
                      type="date"
                      error={errors.entryDate}
                    />
                    <FieldGroup
                      name="memo"
                      label={i18n.t('Memo')}
                    />
                  </FieldGroupRow>

                  <SectionTitle>{i18n.t('Journal Lines')}</SectionTitle>

                  {!queryProps ? (
                    <Loader />
                  ) : (
                    <LinesWrapper>
                      <LineRow>
                        <LineField flex={3}>
                          <LineLabel>{i18n.t('Account')}</LineLabel>
                        </LineField>
                        <LineField flex={1}>
                          <LineLabel>{i18n.t('Debit')}</LineLabel>
                        </LineField>
                        <LineField flex={1}>
                          <LineLabel>{i18n.t('Credit')}</LineLabel>
                        </LineField>
                        <LineField flex={2}>
                          <LineLabel>{i18n.t('Memo')}</LineLabel>
                        </LineField>
                        <div style={{ width: 28 }} />
                      </LineRow>
                      {lines.map((line, index) => (
                        <LineRow key={index}>
                          <LineField flex={3}>
                            <LineSelect
                              value={line.glAccountId}
                              onChange={e =>
                                handleLineChange(
                                  index,
                                  'glAccountId',
                                  e.target.value,
                                )
                              }
                            >
                              <option value="">
                                {i18n.t('Select Account...')}
                              </option>
                              {queryProps.glAccounts.edges.map(({ node }) => (
                                <option key={node.id} value={node.id}>
                                  {node.accountNumber} - {node.name}
                                </option>
                              ))}
                            </LineSelect>
                          </LineField>
                          <LineField flex={1}>
                            <LineInput
                              type="number"
                              step="0.01"
                              min="0"
                              value={line.debit}
                              placeholder="0.00"
                              onChange={e =>
                                handleLineChange(
                                  index,
                                  'debit',
                                  e.target.value,
                                )
                              }
                            />
                          </LineField>
                          <LineField flex={1}>
                            <LineInput
                              type="number"
                              step="0.01"
                              min="0"
                              value={line.credit}
                              placeholder="0.00"
                              onChange={e =>
                                handleLineChange(
                                  index,
                                  'credit',
                                  e.target.value,
                                )
                              }
                            />
                          </LineField>
                          <LineField flex={2}>
                            <LineInput
                              type="text"
                              value={line.memo}
                              placeholder={i18n.t('Line memo')}
                              onChange={e =>
                                handleLineChange(
                                  index,
                                  'memo',
                                  e.target.value,
                                )
                              }
                            />
                          </LineField>
                          <RemoveButton
                            type="button"
                            onClick={() => removeLine(index)}
                            disabled={lines.length <= 2}
                          >
                            <Icon type="circle-close" size={14} />
                          </RemoveButton>
                        </LineRow>
                      ))}

                      <AddLineButton type="button" onClick={addLine}>
                        + {i18n.t('Add Line')}
                      </AddLineButton>

                      <TotalsRow>
                        <div>
                          <TotalLabel>{i18n.t('Total Debits')}: </TotalLabel>
                          <TotalValue balanced={isBalanced}>
                            {formatCurrency(totalDebit)}
                          </TotalValue>
                        </div>
                        <div>
                          <TotalLabel>{i18n.t('Total Credits')}: </TotalLabel>
                          <TotalValue balanced={isBalanced}>
                            {formatCurrency(totalCredit)}
                          </TotalValue>
                        </div>
                      </TotalsRow>
                    </LinesWrapper>
                  )}
                </ModalBody>
                <ModalFooter>
                  <Button onClick={onClose}>
                    {i18n.t('Cancel')}
                  </Button>
                  <Button
                    type="submit"
                    theme="blue"
                    disabled={!isValid || !isBalanced}
                    loading={isSubmitting}
                  >
                    {i18n.t('Create Entry')}
                  </Button>
                </ModalFooter>
              </Form>
            )}
          />
        </Modal>
      )}
    />
  );
};

export default AddJournalEntryModal;
